/**
 * Grid Tool - Grid Line Creation
 *
 * Click-click tool: two clicks define the grid line's horizontal extent.
 * The datum is registered through the kernel's addGrid() so it lands in
 * project.grids — the map the GridRenderer actually draws from — with an
 * auto-incrementing numeric name (1, 2, 3...).
 */

import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class GridTool extends BaseTool {
    id = 'grid';
    name = 'Grid';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    /**
     * Tools are module-level singletons: staged sketch state survives
     * deactivation (Esc mid-draw) and would leak into the NEXT grid session,
     * so the first click would draw from a stale anchor. Reset on activate.
     */
    protected onActivate(): void {
        this.startPoint = null;
        this.currentPoint = null;
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
            this.pushPreview();
        } else {
            this.createGrid(this.startPoint, snapped);
            this.startPoint = null;
            this.currentPoint = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.pushPreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private pushPreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'grid',
                active: true,
                start: this.startPoint,
                end: this.currentPoint,
                color: PREVIEW_COLOR
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createGrid(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        // A second click on the already-snapped start point (double-click or
        // no movement) must not commit a zero-length datum: the renderer
        // draws an invisible line and the failure reads as "nothing happened".
        // Keep the tool armed so the next click places the real end point.
        if (start.distanceTo(end) < 0.001) return;

        // Grids are plan datums: they live in the XY plane at z=0 regardless
        // of the click height, so the drawn line matches the GridRenderer
        // (which draws at z=0.01).
        this.context.bimStore.addGrid(this.nextGridName(), [start.x, start.y], [end.x, end.y]);
    }

    private nextGridName(): string {
        if (!this.context) return '1';
        let max = 0;
        this.context.bimStore.project.grids.forEach(grid => {
            const num = parseInt(grid.name, 10);
            if (!Number.isNaN(num)) max = Math.max(max, num);
        });
        return `${max + 1}`;
    }
}

// BIM-style datum color for the sketch preview (kept here as the tool-side
// token; the viewport preview branch reads the same value from PREVIEW_COLORS).
const PREVIEW_COLOR = '#c9520f';
